import type React from 'react'
import { useState, useMemo, useEffect } from 'react'
import { GraviButton, Horizontal, Vertical, Texto, MaterialIcon } from '@gravitate-js/excalibrr'
import { CheckCircleOutlined, ImportOutlined } from '@ant-design/icons'
import { Popconfirm, Popover, Select, Tooltip } from 'antd'
import type { Match, MatchInvoice } from '@api/useSupplierReconciliation/types/match'
import type { ICellRendererParams } from 'ag-grid-community'
import { useNavigate } from 'react-router-dom'
import type { SupplierReconciliationMetadataResponse } from '@/api/useMetadata/types'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { useReasonCodes } from '../../hooks/useReasonCodes'
import type { LabeledValue } from 'antd/es/select'
import { useMatchActions } from '../../hooks'
import { getUnmatchInvoicesAndBols } from '../../utils/util'
import { getCanUnmatch } from '../../Detail/components/MatchDetailPane/util'

interface MatchActionsRendererProps extends ICellRendererParams<Match> {
  data?: Match
}

export const MatchActionsRenderer: React.FC<MatchActionsRendererProps> = (props) => {
  const { data } = props
  const { scopes } = useSecurityContext()
  const [loadingApprove, setLoadingApprove] = useState(false)
  const [loadingHold, setLoadingHold] = useState(false)
  const [loadingVoid, setLoadingVoid] = useState(false)
  const navigate = useNavigate()

  const { handleMerge } = useMatchActions()

  // Access the context and methods from the grid
  const context = props.context || {}

  // State for reason code
  const [reasonCode, setReasonCode] = useState<string>('')

  // State for popover visibility
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [holdPopoverVisible, setHoldPopoverVisible] = useState(false)
  const [holdReason, setHoldReason] = useState<LabeledValue | null>(null)

  // Check if user has bulk approve permission
  const hasBulkApprovePermission = scopes?.supplier_rec?.bulk_approve || false

  // Check if approval is blocked by delivery reconciliation mode
  const isBlockedByDeliveryRec = useMemo(() => {
    // Get model config from grid context
    const modelConfig = context.modelConfig
    const deliveryReconciliationMode = modelConfig?.supplier_reconciliation?.delivery_reconciliation_mode

    if (deliveryReconciliationMode !== 'block_approval') return false
    // Check if match has delivery reconciliation warnings
    // Note: This assumes warnings come as part of data.warnings array
    // Update the warning type check based on actual API implementation
    return data.warnings?.some((w) => w.type === 'delivery_reconciliation') || false
  }, [context.modelConfig, data.warnings])

  // Get separated reason codes using the new hook
  const metadata: SupplierReconciliationMetadataResponse | undefined = context.metadata
  const { approvalReasonCodeOptions, holdReasonCodeOptions } = useReasonCodes(metadata)

  // Set default reason code when options are loaded
  useEffect(() => {
    if (approvalReasonCodeOptions.length > 0 && !reasonCode) {
      setReasonCode(approvalReasonCodeOptions[0].value)
    }
  }, [approvalReasonCodeOptions, reasonCode])

  // Make sure data exists before accessing properties
  if (!data) {
    return null // Return nothing if no data
  }

  // Determine if approve button should be enabled
  const canApprove = data.status === 'unapproved' && hasBulkApprovePermission && !isBlockedByDeliveryRec
  const canUnmatch = getCanUnmatch(data)

  // Determine if hold/unhold button should be shown
  const isOnHold = data.status === 'hold'

  // Helper function to get approval tooltip message
  const getApprovalTooltip = () => {
    if (!hasBulkApprovePermission) return 'You do not have permission to approve'
    if (isBlockedByDeliveryRec) return 'Approval Blocked: Delivery Reconciliation errors must be resolved'
    if (data.status !== 'unapproved') return 'Match must be unapproved to enable approval'

    // Check if delivery reconciliation errors exist but blocking is disabled
    const modelConfig = context.modelConfig
    const deliveryReconciliationMode = modelConfig?.supplier_reconciliation?.delivery_reconciliation_mode
    const hasDeliveryRecError = data.warnings?.some((w) => w.type === 'delivery_reconciliation')

    if (hasDeliveryRecError && deliveryReconciliationMode !== 'block_approval') {
      return 'Approve Match: Delivery Reconciliation errors present but blocking is disabled'
    }

    return 'Approve Match'
  }

  // Determine if void/unvoid button should be shown using official status field
  const hasInvoice = data.invoice_numbers && data.invoice_numbers.length > 0
  const hasBol =
    data.bol_numbers && data.bol_numbers.filter((bol) => bol.id !== null && bol.id !== undefined).length > 0
  const isInvoiceVoided = data.status === 'void'

  // Show void button for non-void matches with invoices and no BOLs
  // Show restore button for void matches
  const canShowVoidActions = isInvoiceVoided || (data.status === 'unmatched' && hasInvoice && !hasBol)

  // Handle approve action
  const handleApprove = async () => {
    setLoadingApprove(true)
    try {
      if (context.onApproveMatch) {
        await context.onApproveMatch(data, reasonCode)
        setPopoverVisible(false) // Close popover after successful approval
      }
    } finally {
      setLoadingApprove(false)
    }
  }

  // Handle cancel action
  const handleCancel = () => {
    setPopoverVisible(false)
  }

  // Handle hold/unhold action
  const handleHoldToggle = async () => {
    setLoadingHold(true)
    try {
      if (isOnHold && context.onUnholdMatch) {
        await context.onUnholdMatch(data)
      } else if (context.onHoldMatch) {
        await context.onHoldMatch(data, holdReason?.label?.toString() || undefined)
        setHoldPopoverVisible(false)
        setHoldReason(null)
      }
    } finally {
      setLoadingHold(false)
    }
  }

  // Handle hold cancel action
  const handleHoldCancel = () => {
    setHoldPopoverVisible(false)
    setHoldReason(null)
  }

  // Helper function to get invoice ID from match data
  const getInvoiceId = () => {
    // For active invoices, get invoice ID from standard invoice_numbers array
    if (data?.invoice_numbers && data.invoice_numbers.length > 0) {
      const firstInvoice = data.invoice_numbers[0]
      // Try different possible ID fields
      const invoiceId = firstInvoice?.id || firstInvoice?.invoice_id || firstInvoice?._id
      return invoiceId
    }

    // For voided invoices, the invoice data is in the details array
    if (data?.details && data.details.length > 0) {
      const firstDetail = data.details[0]
      const invoiceId = firstDetail?.invoice_id
      if (invoiceId) {
        return invoiceId
      }
    }

    return null
  }

  // Handle void invoice action
  const handleVoidInvoice = async () => {
    setLoadingVoid(true)
    try {
      if (context.onVoidInvoice) {
        const invoiceId = getInvoiceId()
        if (invoiceId) {
          await context.onVoidInvoice(invoiceId)
        }
      }
    } finally {
      setLoadingVoid(false)
    }
  }

  // Handle unvoid invoice action
  const handleUnvoidInvoice = async () => {
    setLoadingVoid(true)
    try {
      if (context.onUnvoidInvoice) {
        const invoiceId = getInvoiceId()
        if (invoiceId) {
          await context.onUnvoidInvoice(invoiceId)
        }
      }
    } catch (error) {
      console.error('Error in handleUnvoidInvoice:', error)
    } finally {
      setLoadingVoid(false)
    }
  }

  // Handle drill-in to match details
  const handleDrillIn = (event?: React.MouseEvent) => {
    if (data && props.api && props.node) {
      // Check if ctrl (Windows/Linux) or cmd (Mac) key is pressed
      const isCtrlOrCmdPressed = event?.ctrlKey || event?.metaKey

      // Check if the match data has a valid ID
      const matchId = data.id

      // If ctrl/cmd is pressed, open in new tab
      if (isCtrlOrCmdPressed) {
        // Build the URL for the match details page
        const matchUrl =
          matchId && matchId !== 'no-match-id'
            ? `/supplier_rec/match_view/${matchId}`
            : `/supplier_rec/match_view/no-match-id`

        window.open(matchUrl, '_blank')
        return // Don't navigate in current tab
      }

      // Otherwise, continue with normal navigation behavior using React Router state
      // Get all displayed rows from the grid
      const displayedRows: Match[] = []
      props.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) {
          displayedRows.push(node.data)
        }
      })

      // Find current index by directly matching the row data
      const currentIndex = displayedRows.findIndex((row) => {
        // Use the same ID logic as the grid's getRowId function
        if (row?.id || row?.match_id || row?._id) {
          return String(row.id || row.match_id || row._id) === String(data.id || data.match_id || data._id)
        }

        // Fallback to composite key comparison for rows without explicit IDs
        const getCompositeKey = (match: Match) => {
          const invoiceIds = (match?.invoice_numbers || [])
            .map((inv: { id?: string; number?: string }) => inv.id || inv.number)
            .join(',')
          const bolNumbers = (match?.bol_numbers || [])
            .map((bol: { id?: string; number?: string }) => bol.id || bol.number)
            .join(',')

          if (invoiceIds || bolNumbers) {
            const invoicePart = invoiceIds || 'no-invoice'
            const bolPart = bolNumbers || 'no-bol'
            return `inv-${invoicePart}-bol-${bolPart}`
          }

          return null
        }

        const currentKey = getCompositeKey(data)
        const rowKey = getCompositeKey(row)

        return currentKey && rowKey && currentKey === rowKey
      })

      // Get any existing filter context from the grid
      const filterContext = context.filterContext || {}

      // Use the actual match ID if available, otherwise use placeholder
      if (matchId && matchId !== 'no-match-id') {
        // Navigate with real match ID - don't pass matchData so the page fetches fresh data
        navigate(`/supplier_rec/match_view/${matchId}`, {
          state: {
            displayedRows,
            currentIndex,
            filterContext,
            metadata: context.metadata, // Pass metadata to detail page
            // Don't pass matchData here - let the page fetch it via API
          },
        })
      } else {
        // Fallback to placeholder ID and pass the full data
        navigate(`/supplier_rec/match_view/no-match-id`, {
          state: {
            displayedRows,
            currentIndex,
            filterContext,
            matchData: data, // Pass the full match data to the detail page
            noMatchId: true, // Flag to indicate there's no real match ID
            metadata: context.metadata, // Pass metadata to detail page
          },
        })
      }
    }
  }

  function handleUnmatch(match: Match) {
    const { invoices, bols } = getUnmatchInvoicesAndBols(match)
    handleMerge(match.id, { invoices, bols }, true)
  }

  // Content for Approve Popover with variance information
  const approvePopoverContent = (
    <Vertical gap={12} style={{ width: 300 }}>
      <Vertical gap={8}>
        <Texto category='p2' weight='600'>
          Select Approval Reason:
        </Texto>
        <Select
          style={{ width: '100%' }}
          data-cy='approve-reason-select'
          placeholder='Choose a reason for approval'
          value={reasonCode}
          onChange={(value) => setReasonCode(value)}
          options={approvalReasonCodeOptions}
          loading={approvalReasonCodeOptions.length === 0}
          showSearch
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          disabled={approvalReasonCodeOptions.length === 0}
        />
      </Vertical>

      {(() => {
        // Calculate filtered warnings for variance details
        const varianceWarnings =
          data?.warnings?.filter(
            (w) =>
              w.type !== 'movement_validation' &&
              w.type !== 'delivery_reconciliation' &&
              w.threshold !== undefined &&
              w.threshold !== null &&
              w.threshold !== 0
          ) || []

        // Calculate delivery reconciliation warnings
        const deliveryRecWarnings = data?.warnings?.filter((w) => w.type === 'delivery_reconciliation') || []

        return (
          <>
            {/* Display variance details only when variance warnings exist */}
            {varianceWarnings.length > 0 && (
              <Vertical
                gap={8}
                style={{
                  padding: 12,
                  background: 'var(--theme-warning-dim)',
                  borderRadius: 4,
                  border: '1px solid var(--theme-warning)',
                }}
              >
                <Texto category='p2' weight='600' style={{ color: 'var(--theme-warning)' }}>
                  Variance Details:
                </Texto>
                {varianceWarnings.map((warning, index) => (
                  <Vertical
                    key={warning.id || `warning-${index}`}
                    gap={4}
                    style={{
                      paddingTop: index > 0 ? 8 : 0,
                      borderTop: index > 0 ? '1px solid var(--theme-separator)' : 'none',
                    }}
                  >
                    <Texto category='p3' weight='500'>
                      {warning.type
                        .replace(/^by_/, '')
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Texto>
                    <Texto category='p3' appearance='hint'>
                      {warning.message}
                    </Texto>
                    <Horizontal gap={16}>
                      <Texto category='p3'>
                        <span style={{ color: 'var(--theme-text-secondary)' }}>Variance:</span>{' '}
                        <span style={{ fontWeight: 600, color: 'var(--theme-warning)' }}>
                          {warning.variance?.toFixed(2) || '0'}
                          {warning.unit}
                        </span>
                      </Texto>
                      <Texto category='p3'>
                        <span style={{ color: 'var(--theme-text-secondary)' }}>Threshold:</span>{' '}
                        <span style={{ fontWeight: 600 }}>
                          {warning.threshold?.toFixed(2) || '0'}
                          {warning.unit}
                        </span>
                      </Texto>
                    </Horizontal>
                  </Vertical>
                ))}
              </Vertical>
            )}

            {/* Display delivery reconciliation warnings */}
            {deliveryRecWarnings.length > 0 && (
              <Vertical
                gap={8}
                style={{
                  padding: 12,
                  background: 'var(--theme-warning-dim)',
                  borderRadius: 4,
                  border: '1px solid var(--theme-warning)',
                }}
              >
                <Horizontal gap={8} verticalCenter>
                  <MaterialIcon icon='local_shipping' style={{ color: 'var(--theme-warning)' }} size={16} />
                  <Texto category='p2' weight='600' style={{ color: 'var(--theme-warning)' }}>
                    Delivery Reconciliation
                  </Texto>
                </Horizontal>
                <Texto category='p3' appearance='hint'>
                  Delivery reconciliation errors present
                </Texto>
              </Vertical>
            )}
          </>
        )
      })()}

      <Horizontal gap={8} justifyContent='flex-end' style={{ marginTop: 8 }}>
        <GraviButton appearance='outlined' buttonText='Cancel' onClick={handleCancel} data-cy='match-approve-cancel' />
        <GraviButton
          theme2
          buttonText='Approve'
          disabled={!reasonCode}
          loading={loadingApprove}
          onClick={handleApprove}
          data-cy='match-approve-confirm'
        />
      </Horizontal>
    </Vertical>
  )

  // Content for Hold Popover with reason selection
  const holdPopoverContent = (
    <Vertical gap={8} style={{ width: 280 }}>
      <Texto weight='bold'>Mark On Hold</Texto>
      <Texto appearance='hint'>
        Temporarily pause processing while awaiting supplier clarification or additional information.
      </Texto>

      <Texto>Select hold reason (optional):</Texto>
      <Select
        style={{ width: '100%' }}
        data-cy='hold-reason-select'
        value={holdReason}
        onChange={(value) => setHoldReason(value)}
        options={holdReasonCodeOptions}
        placeholder='Select hold reason'
        loading={holdReasonCodeOptions.length === 0}
        showSearch
        allowClear
        filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
        labelInValue
      />

      <Horizontal gap={8} style={{ justifyContent: 'flex-end', marginTop: 8 }}>
        <GraviButton buttonText='Cancel' type='text' onClick={handleHoldCancel} data-cy='match-hold-cancel' />
        <GraviButton
          buttonText='Mark On Hold'
          theme1
          onClick={handleHoldToggle}
          loading={loadingHold}
          data-cy='match-hold-confirm'
        />
      </Horizontal>
    </Vertical>
  )

  return (
    <Horizontal
      gap={8}
      verticalCenter
      style={{ justifyContent: 'space-between', overflow: 'visible', height: '100%', width: '100%' }}
    >
      {/* Hold/Unhold Button - moved to far right with pause icon */}
      {data.status !== 'approved' &&
        (isOnHold ? (
          <Tooltip title='Remove Hold'>
            <GraviButton
              onClick={handleHoldToggle}
              loading={loadingHold}
              appearance='outline'
              theme2
              size='small'
              icon={<MaterialIcon icon='close' style={{ fontSize: 16 }} className='pt-1' />}
              data-cy='match-action-unhold'
            />
          </Tooltip>
        ) : (
          <Tooltip title='Mark On Hold'>
            <Popover
              content={holdPopoverContent}
              title={null}
              trigger='click'
              placement='left'
              open={holdPopoverVisible}
              onOpenChange={setHoldPopoverVisible}
            >
              <GraviButton
                loading={loadingHold}
                appearance='outline'
                size='small'
                icon={<MaterialIcon icon='pause' style={{ fontSize: 16 }} className='pt-1' />}
                data-cy='match-action-hold'
              />
            </Popover>
          </Tooltip>
        ))}
      {/* Approve Button with Popover OR Void Button - always reserve space */}
      {data.status === 'unapproved' && (!hasBulkApprovePermission || isBlockedByDeliveryRec) ? (
        <Tooltip title={getApprovalTooltip()}>
          <GraviButton
            size='small'
            icon={<CheckCircleOutlined style={{ fontSize: 14 }} className='pt-1' />}
            type='text'
            disabled={true}
          />
        </Tooltip>
      ) : canApprove ? (
        <Tooltip title={getApprovalTooltip()}>
          <Popover
            content={approvePopoverContent}
            title={null}
            trigger='click'
            placement='left'
            open={popoverVisible}
            onOpenChange={setPopoverVisible}
          >
            <GraviButton
              size='small'
              icon={<CheckCircleOutlined style={{ fontSize: 14 }} className='pt-1' />}
              type='text'
              theme2
              loading={loadingApprove}
              disabled={!canApprove}
              data-cy='match-action-approve'
            />
          </Popover>
        </Tooltip>
      ) : !hasBulkApprovePermission &&
        (isInvoiceVoided || ((data.status === 'unmatched' || data.status === 'hold') && hasInvoice && !hasBol)) ? (
        /* Show disabled void/unvoid button when no permission */
        <Tooltip title='You do not have permission to void/restore invoices'>
          <GraviButton
            size='small'
            icon={
              <MaterialIcon icon={isInvoiceVoided ? 'refresh' : 'cancel'} style={{ fontSize: 16 }} className='pt-1' />
            }
            type='text'
            disabled={true}
          />
        </Tooltip>
      ) : canShowVoidActions ? (
        /* Show void or unvoid based on official invoice status */
        isInvoiceVoided ? (
          <Tooltip title='Restore Voided Invoice'>
            <Popconfirm
              title='Restore Invoice'
              description='Are you sure you want to restore this voided invoice?'
              onConfirm={handleUnvoidInvoice}
              okText='Yes, Restore'
              cancelText='Cancel'
            >
              <GraviButton
                size='small'
                icon={<MaterialIcon icon='refresh' style={{ fontSize: 16 }} className='pt-1' />}
                type='text'
                appearance='outlined'
                loading={loadingVoid}
                data-cy='match-action-unvoid'
              />
            </Popconfirm>
          </Tooltip>
        ) : (
          <Tooltip title='Void Invoice'>
            <Popconfirm
              title='Void Invoice'
              description='Are you sure you want to void this invoice?'
              onConfirm={handleVoidInvoice}
              okText='Yes, Void'
              cancelText='Cancel'
            >
              <GraviButton
                size='small'
                icon={<MaterialIcon icon='cancel' style={{ fontSize: 16 }} className='pt-1' />}
                type='text'
                appearance='outlined'
                loading={loadingVoid}
                data-cy='match-action-void'
              />
            </Popconfirm>
          </Tooltip>
        )
      ) : (
        <div style={{ width: 24, height: 32 }} />
      )}

      <div>
        {canUnmatch && (
          <Tooltip title='Unmatch'>
            <Popconfirm
              title='Unmatch'
              description='Are you sure you want to unmatch?'
              onConfirm={() => handleUnmatch(data)}
              okText='Yes, Unmatch'
              cancelText='Cancel'
            >
              <GraviButton size='small' icon={<MaterialIcon className='pt-1' icon='link_off' />} type='text' />
            </Popconfirm>
          </Tooltip>
        )}
      </div>

      {/* Match Drill-in Button - moved to far right with arrow_forward icon */}
      <Tooltip title='View Match Details'>
        <GraviButton
          size='small'
          icon={<MaterialIcon icon='arrow_forward' style={{ fontSize: 16 }} className='pt-1' />}
          type='text'
          onClick={handleDrillIn}
          data-cy='match-details-button'
        />
      </Tooltip>
    </Horizontal>
  )
}

// Separate renderer for invoice actions in the detail section
export const InvoiceActionsRenderer: React.FC<{
  invoice: MatchInvoice
  match: Match
  context: {
    onVoidInvoice?: (invoiceId: string) => Promise<void>
    onUnvoidInvoice?: (invoiceId: string) => Promise<void>
  }
}> = ({ invoice, match, context }) => {
  const [loading, setLoading] = useState(false)

  const isVoided = invoice.voided === true

  const handleVoidToggle = async () => {
    setLoading(true)
    try {
      if (isVoided && context.onUnvoidInvoice) {
        await context.onUnvoidInvoice(invoice.id, match)
      } else if (context.onVoidInvoice) {
        await context.onVoidInvoice(invoice.id, match)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Popconfirm
      title={isVoided ? 'Restore this voided invoice?' : 'Void this invoice?'}
      onConfirm={handleVoidToggle}
      okText='Yes'
      cancelText='No'
    >
      <GraviButton
        buttonText={isVoided ? 'Unvoid' : 'Void'}
        icon={<ImportOutlined style={{ fontSize: 16 }} className='pt-1' />}
        type='text'
        loading={loading}
        ghost={isVoided}
        theme2={isVoided}
        size='small'
      />
    </Popconfirm>
  )
}
